import { Divider, Flex, Text } from '@mantine/core';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDetailsRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsRow/InvestorDetailsRow.tsx';
import { DetailsTileStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DetailsTile.styles.tsx';
import { formatEmploymentDate } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/EmploymentRecordsCard/employmentRecords.utils.ts';
import { EmploymentRecordRes } from '@dealstack/shared/lib/generated';
import { EmploymentRecordItemRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/EmploymentRecordsCard/EmploymentRecordItem.types.ts';

interface Props {
	record: EmploymentRecordRes;
	employerName?: string;
}

export const EmploymentRecordItem = ({ record, employerName }: Props) => {
	const tMipManage = useTranslationMipManage();

	const jobTitle = record.jobTitle?.trim();
	const employeeReference = record.employeeReference?.trim();
	const startDate = formatEmploymentDate(record.startDate);
	const endDate = formatEmploymentDate(record.endDate);

	const rows: EmploymentRecordItemRow[] = [];

	if (employeeReference) {
		rows.push({
			key: 'employee-reference',
			label: tMipManage('investors.details.employment_card.employee_id'),
			value: employeeReference,
			copyValue: employeeReference,
			dataTestId: makeComplexDataTestId(
				TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_EMPLOYMENT_EMPLOYEE_ID,
				record.id
			),
		});
	}

	if (startDate) {
		rows.push({
			key: 'start-date',
			label: tMipManage('investors.details.employment_card.start_date'),
			value: startDate,
		});
	}

	if (endDate) {
		rows.push({
			key: 'end-date',
			label: tMipManage('investors.details.employment_card.end_date'),
			value: endDate,
		});
	}

	return (
		<DetailsTileStyles
			data-testid={makeComplexDataTestId(
				TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_EMPLOYMENT_RECORD,
				record.id
			)}
		>
			{!!jobTitle && (
				<Text fz={14} fw={600} c="dark.8">
					{jobTitle}
				</Text>
			)}

			<Text fz={12} c="dark.9" truncate>
				{employerName || LONG_DASH}
			</Text>

			{rows.length > 0 && <Divider color="gray.2" mt="0.25rem" mb="0.25rem" />}

			<Flex direction="column" gap="0.25rem">
				{rows.map((row) => (
					<InvestorDetailsRow
						key={row.key}
						size="sm"
						padding="0"
						label={row.label}
						value={row.value}
						copyValue={row.copyValue}
						dataTestId={row.dataTestId}
					/>
				))}
			</Flex>
		</DetailsTileStyles>
	);
};
